import React, { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMobileDetection } from '../hooks/useMobileDetection';
import { cn } from '../utils/cn';
import { Home, List as ListIcon, Plus, Calendar, ListChecks } from 'lucide-react';
import UserMenu from './UserMenu';
import VersionInfo from './VersionInfo';
import MobileLayout from './MobileLayout';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { isMobile } = useMobileDetection();

  if (isMobile) return <MobileLayout>{children}</MobileLayout>;

  const navigation = [
    { name: 'Главная', href: '/', icon: Home },
    { name: 'Списки', href: '/lists', icon: ListIcon },
    { name: 'Календарь', href: '/calendar', icon: Calendar },
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-surface-muted">
      <header className="app-header bg-surface/85 backdrop-blur border-b border-line">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center h-16 gap-8">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-primary-600 text-white flex items-center justify-center">
                <ListChecks className="w-5 h-5" strokeWidth={2.25} />
              </div>
              <span className="text-base font-semibold tracking-tight text-ink">xLists</span>
            </Link>

            <nav className="flex items-center gap-1">
              {navigation.map(({ name, href, icon: Icon }) => (
                <Link
                  key={href}
                  to={href}
                  className={cn(
                    'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    isActive(href)
                      ? 'bg-surface-muted text-ink'
                      : 'text-ink-muted hover:text-ink hover:bg-surface-muted'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {name}
                </Link>
              ))}
            </nav>

            <div className="flex-1" />

            <Link to="/new-list" className="btn-primary btn-sm">
              <Plus className="w-4 h-4" />
              Новый список
            </Link>

            <UserMenu />
          </div>
        </div>
      </header>

      <main className="max-w-6xl w-full mx-auto px-6 pb-8 app-main">{children}</main>

      <footer className="py-6 border-t border-line bg-surface">
        <div className="max-w-6xl mx-auto px-6">
          <VersionInfo />
        </div>
      </footer>
    </div>
  );
};

export default Layout;
